import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { PaymentService } from '../../services/payments/payment/payment.service';
import { TransactionDetailResponse } from '../../models/payment/transactionDetailResponse.model';
import { MessageService } from 'primeng/api';

interface PaymentFailureData {
  orderId?: string;
  transactionId?: string;
  failureDate?: string;
  customer: {
    name: string;
    email: string;
    phone: string;
    address: string;
  };
  package: {
    name: string;
    services: string[];
  };
  amount: number;
  failureReason?: string;
}

@Component({
  selector: 'app-payment-failure',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  templateUrl: './payment-failure.component.html',
  styleUrls: ['./payment-failure.component.scss']
})
export class PaymentFailureComponent implements OnInit {
  private paymentService = inject(PaymentService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);

  paymentFailureData: PaymentFailureData | null = null;
  loading = true;

  ngOnInit(): void {
    const transactionId = this.route.snapshot.params['transactionId'];
    const reason = this.route.snapshot.queryParams['reason'];

    if (transactionId) {
      this.loadTransactionData(transactionId, reason);
    } else {
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Transaction ID is missing'
      });
    }
  }

  private loadTransactionData(transactionId: string, reason?: string): void {
    this.paymentService.getTransaction(transactionId).subscribe({
      next: (data: TransactionDetailResponse) => {
        const isDirectApply = data.packageDetails?.systemAssignedPackageName?.toLowerCase()?.trim() === 'institute direct application' 
                           || data.packageDetails?.packageName?.toLowerCase()?.trim() === 'institute direct application'
                           || data.packageDetails?.systemAssignedPackageName?.toLowerCase()?.includes('direct application')
                           || data.packageDetails?.packageName?.toLowerCase()?.includes('direct application');

        if (isDirectApply) {
          this.router.navigate(['/portal/direct-apply'], { queryParams: { payment: 'review', success: 'False' }});
          return;
        }

        this.mapToPaymentFailureData(data, reason);
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load transaction details. Please try again later.'
        });
        console.error('Error loading transaction:', error);
      }
    });
  }

  private mapToPaymentFailureData(data: TransactionDetailResponse, reason?: string): void {
    this.paymentFailureData = {
      orderId: data.transactionId,
      transactionId: data.transactionId,
      failureDate: this.formatDate(data.paymentDate),
      customer: {
        name: data.customerDetails.name,
        email: data.customerDetails.email,
        phone: data.customerDetails.phone,
        address: data.customerDetails.address
      },
      package: {
        name: data.packageDetails.packageName,
        services: data.packageDetails.servicesIncluded
      },
      amount: data.paymentSummary.totalPaid,
      failureReason: reason || 'Payment was declined. Please check your account balance or contact your bank for more details.'
    };
  }

  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateString;
    }
  }

  tryAgain(): void {
    this.router.navigate(['/pay-now']);
  }

  contactSupport(): void {
    this.router.navigate(['/contact-us']);
  }
}

